export interface WebappDefaultPageRoute {
  path: string;
  block?: string;
  guards?: string[];
  children?: WebappDefaultPageRoute[];
  redirect_to?: string;
}

/**
 * Route tree historically injected by the webapp when a marketplace does not
 * override these pages. It is shared with the CB2 journey creation flow so the
 * authoring catalogue and the visitor runtime cannot drift apart.
 */
export const WEBAPP_DEFAULT_PAGE_ROUTES: WebappDefaultPageRoute[] = [
  {
    path: "",
    block: "main-layout",
    children: [
      {
        path: "",
        redirect_to: "shops",
      },
      {
        path: "shops",
        block: "shop-list-page",
      },
      {
        path: "map",
        block: "map-page",
      },
      {
        path: "from-qr",
        block: "from-qr-page",
      },
      {
        path: "cart",
        block: "cart-page",
        guards: ["AuthGuard"],
        children: [
          {
            path: "items/:itemId",
            block: "cart-item-edit-page",
          },
          {
            path: "products/:shopId/:productId",
            block: "product-add-page",
          },
        ],
      },
      {
        path: "cart/funnel",
        block: "cart-funnel-page",
        guards: ["AuthGuard"],
      },
      {
        path: "validate/shared-order",
        block: "validate-shared-order-page",
        guards: ["AuthGuard"],
      },
      {
        path: "payment",
        block: "payment-page",
        guards: ["AuthGuard", "OrderValidateGuard"],
      },
      {
        path: "payment-confirm",
        block: "payment-confirm-page",
        guards: ["AuthGuard"],
      },
      {
        path: "group-payment",
        block: "group-payment-page",
        guards: ["AuthGuard", "OrderValidateGuard"],
      },
      {
        path: "orders/:orderId/status",
        block: "order-status-page",
        guards: ["AuthGuard"],
      },
      {
        path: "orders/:orderId/details",
        block: "order-details-page",
        guards: ["AuthGuard"],
      },
      {
        path: "account",
        block: "account-page",
        guards: ["AuthGuard"],
      },
      {
        path: "orders",
        block: "orders-page",
        guards: ["AuthGuard"],
      },
      {
        path: "checks",
        block: "checks-page",
        guards: ["AuthGuard"],
      },
      {
        path: "checks/:checkId/details",
        block: "check-details-page",
        guards: ["AuthGuard"],
      },
      {
        path: "pay-at-table",
        block: "pay-at-table-page",
        children: [
          {
            path: "",
            redirect_to: "overview",
          },
          {
            path: "overview",
            block: "pay-at-table-overview-page",
            guards: ["AuthGuard"],
          },
          {
            path: "choose-amount",
            block: "pay-at-table-choose-amount-page",
            guards: ["AuthGuard", "PatCheckGuard"],
          },
          {
            path: "payment",
            block: "pay-at-table-payment-page",
            guards: ["AuthGuard", "PatCheckGuard"],
          },
          {
            path: "payment-confirmation/:id",
            block: "pay-at-table-payment-confirmation-page",
            guards: ["AuthGuard", "PatCheckGuard"],
          },
        ],
      },
      {
        path: ":shopCity/:shopType/:shopSlug",
        block: "shop-page",
        children: [
          {
            path: "products/:shopId/:productId",
            block: "product-add-page",
          },
        ],
      },
    ],
  },
  {
    path: ":shopCity/:shopType/:shopSlug/menu",
    block: "simple-layout",
    children: [
      {
        path: "",
        block: "shop-digital-menu-page",
        children: [
          {
            path: "products/:shopId/:productId",
            block: "digital-menu-product-page",
          },
        ],
      },
    ],
  },
  {
    path: "debug/network-connection",
    block: "debug-network-connection-page",
  },
];

/**
 * Routes a CB2 journey does not author, but the webapp still navigates to.
 * Cart item edit opens `item-edit-modal` in place, so it is not a route.
 * Nested under Main Layout like the v1 tree so `injectDefaultPages` can merge
 * the missing children. Does not replace `WEBAPP_DEFAULT_PAGE_ROUTES`.
 *
 * `cart/funnel` is a fallback for journeys whose funnel page was deleted: the
 * cart still navigates there, so an empty `cart-funnel-page` (no steps) is
 * injected and it forwards the visitor straight to the payment page.
 *
 * Account, orders, checks and the Stripe return page stay reachable when the
 * journey does not define them. A path the journey already has is left as-is.
 */
export const WEBAPP_CB2_DEFAULT_PAGE_ROUTES: WebappDefaultPageRoute[] = [
  {
    path: "",
    children: [
      {
        path: "cart",
        children: [
          {
            path: "products/:shopId/:productId",
            block: "product-add-page",
          },
        ],
      },
      {
        path: "cart/funnel",
        block: "cart-funnel-page",
        guards: ["AuthGuard"],
      },
      {
        path: "payment-confirm",
        block: "payment-confirm-page",
        guards: ["AuthGuard"],
      },
      {
        path: "account",
        block: "account-page",
        guards: ["AuthGuard"],
      },
      {
        path: "orders",
        block: "orders-page",
        guards: ["AuthGuard"],
      },
      {
        path: "checks",
        block: "checks-page",
        guards: ["AuthGuard"],
      },
      {
        path: "checks/:checkId/details",
        block: "check-details-page",
        guards: ["AuthGuard"],
      },
      {
        path: ":shopCity/:shopType/:shopSlug",
        children: [
          {
            path: "products/:shopId/:productId",
            block: "product-add-page",
          },
        ],
      },
    ],
  },
];

/** Root component type for every block referenced by the default route tree. */
export const WEBAPP_DEFAULT_PAGE_ROOT_TYPES: Readonly<Record<string, string>> =
  {
    "main-layout": "MainLayoutComponent",
    "simple-layout": "MainLayoutComponent",
    "shop-list-page": "ShopSearchPageComponent",
    "map-page": "ShopsMapPageComponent",
    "from-qr-page": "RedirectPageComponent",
    "cart-page": "CartPageComponent",
    "cart-item-edit-page": "ItemEditPageComponent",
    "product-add-page": "ProductAddPageComponent",
    "cart-funnel-page": "CartFunnelPageComponent",
    "validate-shared-order-page": "ValidateSharedOrderPageComponent",
    "payment-page": "PaymentPageComponent",
    "payment-confirm-page": "PaymentConfirmPageComponent",
    "group-payment-page": "GroupPaymentPageComponent",
    "order-status-page": "OrderStatusPageComponent",
    "order-details-page": "OrderDetailsPageComponent",
    "account-page": "AccountPageComponent",
    "orders-page": "OrdersPageComponent",
    "checks-page": "ChecksPageComponent",
    "check-details-page": "CheckDetailsPageComponent",
    "pay-at-table-page": "PatPageComponent",
    "pay-at-table-overview-page": "GenericPageComponent",
    "pay-at-table-choose-amount-page": "PatChooseAmountPageComponent",
    "pay-at-table-payment-page": "PatPaymentPageComponent",
    "pay-at-table-payment-confirmation-page":
      "PatPaymentConfirmationPageComponent",
    "shop-page": "ShopPageComponent",
    "shop-digital-menu-page": "ShopDigitalMenuPageComponent",
    "digital-menu-product-page": "ProductAddPageComponent",
    "debug-network-connection-page": "GenericPageComponent",
  };

/** Component types for blocks referenced by a default page slot composition. */
export const WEBAPP_DEFAULT_CHILD_BLOCK_TYPES: Readonly<
  Record<string, string>
> = {
  "header-block": "HeaderBlockComponent",
  "shop-around-search-block": "ShopAroundListHeaderBlockComponent",
  "shop-around-list-block": "ShopAroundListBodyBlockComponent",
  "shops-map-block": "ShopsMapBlockComponent",
  "cart-header-block": "CartHeaderBlockComponent",
  "cart-suggestion-cards-block": "CartSuggestionCardsBlockComponent",
  "cart-item-list-block": "CartItemListBlockComponent",
  "cart-amount-block": "CartMerchantAmountBlockComponent",
  "cart-on-site-location-block": "CartOnSiteLocationBlockComponent",
  "cart-voucher-block": "CartVoucherBlockComponent",
  "cart-tip-picker-block": "CartTipPickerBlockComponent",
  "cart-additional-information-block":
    "CartAdditionalInformationBlockComponent",
  "como-join-loyalty-block": "ComoJoinLoyaltyBlockComponent",
  "como-cart-gifts-block": "ComoAssetListBlockComponent",
  "como-cart-credit-balance-card-block": "ComoCreditBalanceCardBlockComponent",
  "zerosix-cart-gifts-block": "ZerosixAssetListBlockComponent",
  "pongo-cart-gifts-block": "PongoAssetListBlockComponent",
  "validate-shared-order-header-block":
    "ValidateSharedOrderHeaderBlockComponent",
  "validate-shared-order-body-block": "ValidateSharedOrderBodyBlockComponent",
  "payment-header-block": "PaymentPageHeaderBlockComponent",
  "payment-method-picker-block": "PaymentMethodPickerBlockComponent",
  "group-payment-header-block": "GroupPaymentPageHeaderBlockComponent",
  "group-payment-list-block": "GroupPaymentListBlockComponent",
  "order-status-success-header-block": "OrderStatusSuccessHeaderBlockComponent",
  "order-status-follow-order-card-block":
    "OrderStatusFollowOrderCardBlockComponent",
  "order-status-block": "OrderStatusBlockComponent",
  "order-details-block": "OrderDetailsBlockComponent",
  "order-invoice-block": "OrderInvoiceBlockComponent",
  "account-block": "AccountPageBlockComponent",
  "orders-block": "OrdersPageBlockComponent",
  "checks-block": "ChecksPageBlockComponent",
  "check-details-block": "CheckDetailsBlockComponent",
  "pay-at-table-overview-page-overview-block": "PatOverviewBlockComponent",
  "shop-header-block": "HeaderBlockComponent",
  "shop-page-hero-block": "ShopHeroImageBlockComponent",
  "shop-page-product-list-block": "ShopProductListBlockComponent",
  "app-header-digital-menu-block": "HeaderDigitalMenuBlockComponent",
  "shop-digital-menu-page-product-list-block": "ShopProductListBlockComponent",
  "debug-network-connection-block": "DebugNetworkConnectionBlockComponent",
};

/** Exact root-slot compositions from the webapp's historical DEFAULT_BLOCKS. */
export const WEBAPP_DEFAULT_PAGE_SLOT_BLOCK_IDS: Readonly<
  Record<string, Readonly<Record<string, readonly string[]>>>
> = {
  "shop-list-page": {
    body: [
      "header-block",
      "shop-around-search-block",
      "shop-around-list-block",
    ],
  },
  "map-page": {
    body: ["header-block", "shops-map-block"],
  },
  "cart-page": {
    top: ["cart-header-block", "cart-suggestion-cards-block"],
    left: ["cart-item-list-block", "cart-amount-block"],
    right: [
      "cart-on-site-location-block",
      "cart-voucher-block",
      "cart-tip-picker-block",
      "cart-additional-information-block",
      "como-join-loyalty-block",
      "como-cart-gifts-block",
      "como-cart-credit-balance-card-block",
      "zerosix-cart-gifts-block",
      "pongo-cart-gifts-block",
    ],
  },
  "validate-shared-order-page": {
    body: [
      "validate-shared-order-header-block",
      "validate-shared-order-body-block",
    ],
  },
  "payment-page": {
    body: ["payment-header-block", "payment-method-picker-block"],
  },
  "payment-confirm-page": {
    body: ["payment-header-block"],
  },
  "group-payment-page": {
    body: ["group-payment-header-block", "group-payment-list-block"],
  },
  "order-status-page": {
    body: [
      "header-block",
      "order-status-success-header-block",
      "order-status-follow-order-card-block",
      "order-status-block",
    ],
  },
  "order-details-page": {
    body: ["header-block", "order-details-block", "order-invoice-block"],
  },
  "account-page": {
    body: ["header-block", "account-block"],
  },
  "orders-page": {
    body: ["header-block", "orders-block"],
  },
  "checks-page": {
    body: ["header-block", "checks-block"],
  },
  "check-details-page": {
    body: ["header-block", "check-details-block"],
  },
  "pay-at-table-overview-page": {
    body: ["pay-at-table-overview-page-overview-block"],
  },
  "shop-page": {
    body: [
      "shop-header-block",
      "shop-page-hero-block",
      "shop-page-product-list-block",
    ],
  },
  "shop-digital-menu-page": {
    body: [
      "app-header-digital-menu-block",
      "shop-page-hero-block",
      "shop-digital-menu-page-product-list-block",
    ],
  },
  "debug-network-connection-page": {
    body: ["header-block", "debug-network-connection-block"],
  },
};
